import { supabase } from '../lib/supabase'
import { sampleInventory } from '../data/sampleInventory'

// Vehicle service to handle all vehicle-related database operations
export class VehicleService {
  constructor() {
    this.tableName = 'vehicles_db2025'
    this.useSampleData = false // Now using real Supabase API
  }

  // Get all vehicles (including sold - for admin use)
  async getAllVehicles() {
    try {
      // Use sample data if flagged or if database connection fails
      if (this.useSampleData) {
        return { data: sampleInventory, error: null }
      }
      
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('VehicleService Error fetching from', this.tableName, ':', error)
        // DO NOT fallback to sample data - throw the error so we can see it
        return { data: [], error }
      }

      // Transform database format to frontend format
      const transformedData = data?.map(this.transformVehicleData) || []
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('VehicleService Exception:', err)
      // DO NOT fallback to sample data - throw the error
      return { data: [], error: err }
    }
  }
  
  // Get available vehicles only (excludes sold - for public display)
  async getAvailableVehicles() {
    try {
      // Use sample data if flagged or if database connection fails
      if (this.useSampleData) {
        // Filter sample data to only show available vehicles
        const availableVehicles = sampleInventory.filter(v => 
          !v.status || v.status !== 'sold'
        )
        return { data: availableVehicles, error: null }
      }
      
      const { data: rawData, error } = await supabase
        .from(this.tableName)
        .select('*')
        .order('created_at', { ascending: false })
      
      // Filter out sold vehicles client-side for reliable case-insensitive matching
      const data = (rawData || []).filter(v => {
        const status = (v.status || '').toLowerCase();
        return !status.includes('sold');
      });

      if (error) {
        console.error('VehicleService Error fetching available vehicles:', error)
        // DO NOT fallback to sample data
        return { data: [], error }
      }

      // Transform database format to frontend format
      const transformedData = data?.map(this.transformVehicleData) || []
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('VehicleService Exception:', err)
      // DO NOT fallback to sample data
      return { data: [], error: err }
    }
  }

  // Get vehicle by ID
  async getVehicleById(id) {
    try {
      console.log('VehicleService: Fetching vehicle by ID:', id)
      
      // Use sample data if flagged or if database connection fails
      if (this.useSampleData) {
        console.log('VehicleService: Using sample inventory data for ID:', id)
        const vehicle = sampleInventory.find(v => v.id === parseInt(id))
        return { data: vehicle, error: vehicle ? null : 'Vehicle not found' }
      }
      
      const lookupId = (typeof id === 'string' && id.includes('-')) ? id : parseInt(id)
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('id', lookupId)
        .single()

      if (error) {
        console.error('VehicleService Error:', error)
        // DO NOT fallback to sample data
        return { data: null, error }
      }

      console.log('VehicleService: Fetched vehicle:', data)
      // Transform database format to frontend format
      const transformedData = data ? this.transformVehicleData(data) : null
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('VehicleService Exception:', err)
      // DO NOT fallback to sample data
      return { data: null, error: err }
    }
  }

  // Get vehicles filtered by vehicle_type values (including sold - for admin)
  async getVehiclesByType(types = []) {
    try {
      // Use sample data if flagged
      if (this.useSampleData) {
        // Sample data lacks explicit vehicle_type, treat as general vehicles
        const data = types.some(t => (t || '').toLowerCase().includes('vehicle'))
          ? sampleInventory
          : []
        return { data, error: null }
      }

      let query = supabase
        .from(this.tableName)
        .select('*')

      if (Array.isArray(types) && types.length > 0) {
        query = query.in('vehicle_type', types)
      }

      const { data, error } = await query
      if (error) {
        console.error('VehicleService getVehiclesByType Error:', error)
        return { data: [], error }
      }

      const transformed = data?.map(this.transformVehicleData) || []
      return { data: transformed, error: null }
    } catch (err) {
      console.error('VehicleService getVehiclesByType Exception:', err)
      return { data: [], error: err }
    }
  }
  
  // Get available vehicles filtered by type (excludes sold - for public display)
  async getAvailableVehiclesByType(types = []) {
    try {
      // Use sample data if flagged
      if (this.useSampleData) {
        // Sample data lacks explicit vehicle_type, treat as general vehicles
        const data = types.some(t => (t || '').toLowerCase().includes('vehicle'))
          ? sampleInventory.filter(v => !v.status || v.status !== 'sold')
          : []
        return { data, error: null }
      }

      let query = supabase
        .from(this.tableName)
        .select('*')

      if (Array.isArray(types) && types.length > 0) {
        query = query.in('vehicle_type', types)
      }

      const { data: rawData, error } = await query
      
      // Filter out sold vehicles client-side for reliable case-insensitive matching
      const data = (rawData || []).filter(v => {
        const status = (v.status || '').toLowerCase();
        return !status.includes('sold');
      });
      if (error) {
        console.error('VehicleService getAvailableVehiclesByType Error:', error)
        return { data: [], error }
      }

      const transformed = data?.map(this.transformVehicleData) || []
      return { data: transformed, error: null }
    } catch (err) {
      console.error('VehicleService getAvailableVehiclesByType Exception:', err)
      return { data: [], error: err }
    }
  }

  // Get a single random available vehicle from the given types (for public display)
  async getRandomVehicleByType(types = []) {
    // Use available vehicles only (excludes sold)
    const { data, error } = await this.getAvailableVehiclesByType(types)
    if (error) return { data: null, error }
    if (!data || data.length === 0) return { data: null, error: null }
    const idx = Math.floor(Math.random() * data.length)
    return { data: data[idx], error: null }
  }

  // Transform database format to frontend format
  transformVehicleData(dbVehicle) {
    if (!dbVehicle) return null
    
    // Parse images if they come as a string (JSON)
    let images = dbVehicle.images || [];
    if (typeof images === 'string') {
      try {
        images = JSON.parse(images);
      } catch (e) {
        console.error('Failed to parse images:', e);
        images = [];
      }
    }
    
    // Parse videos if they come as a string (JSON)
    let videos = dbVehicle.videos || [];
    if (typeof videos === 'string') {
      try {
        videos = JSON.parse(videos);
      } catch (e) {
        console.error('Failed to parse videos:', e);
        videos = [];
      }
    }
    
    return {
      id: dbVehicle.id,
      make: dbVehicle.make,
      model: dbVehicle.model,
      year: dbVehicle.year,
      price: dbVehicle.price,
      deposit_amount: dbVehicle.deposit_amount,
      mileage: dbVehicle.mileage,
      condition: dbVehicle.condition,
      status: dbVehicle.status || 'on-lot',
      vehicleType: (dbVehicle.vehicle_type ? String(dbVehicle.vehicle_type).toLowerCase() : 'vehicle'),
      doors: dbVehicle.doors,
      seating: dbVehicle.seating,
      cityMPG: dbVehicle.city_mpg,
      highwayMPG: dbVehicle.highway_mpg,
      combinedMPG: dbVehicle.combined_mpg,
      statusUpdatedAt: dbVehicle.status_updated_at,
      statusHistory: dbVehicle.status_history || [],
      vin: dbVehicle.vin,
      stockNumber: dbVehicle.stock_number,
      fuelType: dbVehicle.fuel_type,
      transmission: dbVehicle.transmission,
      exteriorColor: dbVehicle.exterior_color,
      interiorColor: dbVehicle.interior_color,
      engine: dbVehicle.engine,
      drivetrain: dbVehicle.drivetrain,
      description: dbVehicle.description,
      features: dbVehicle.features || [],
      images: images,
      videos: videos,
      createdAt: dbVehicle.created_at
    }
  }

  // Create new vehicle
  async createVehicle(vehicleData) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .insert([this.transformToDbFormat(vehicleData)])
        .select()
        .single()

      if (error) {
        console.error('VehicleService Create Error:', error)
        return { data: null, error }
      }

      return { data: this.transformVehicleData(data), error: null }
    } catch (err) {
      console.error('VehicleService Create Exception:', err)
      return { data: null, error: err }
    }
  }

  // Update vehicle
  async updateVehicle(id, vehicleData) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .update(this.transformToDbFormat(vehicleData))
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('VehicleService Update Error:', error)
        return { data: null, error }
      }

      return { data: this.transformVehicleData(data), error: null }
    } catch (err) {
      console.error('VehicleService Update Exception:', err)
      return { data: null, error: err }
    }
  }

  // Update vehicle status (simplified method for status changes)
  async updateVehicleStatus(id, newStatus) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .update({ 
          status: newStatus,
          status_updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('VehicleService Update Status Error:', error)
        return { data: null, error }
      }

      return { data: this.transformVehicleData(data), error: null }
    } catch (err) {
      console.error('VehicleService Update Status Exception:', err)
      return { data: null, error: err }
    }
  }

  // Delete vehicle
  async deleteVehicle(id) {
    try {
      // Delete all related records from existing tables only
      const relatedTables = [
        'deals'
        // Other tables may not exist or have different schemas
      ]

      // Delete from each related table
      for (const table of relatedTables) {
        const { error } = await supabase
          .from(table)
          .delete()
          .eq('vehicle_id', id)
        
        if (error) {
          console.error(`Error deleting from ${table}:`, error)
          // Continue with other tables
        }
      }

      // Now delete the vehicle
      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .eq('id', id)

      if (error) {
        console.error('VehicleService Delete Error:', error)
        return { error }
      }

      return { error: null }
    } catch (err) {
      console.error('VehicleService Delete Exception:', err)
      return { error: err }
    }
  }

  // Transform frontend format to database format
  transformToDbFormat(vehicle) {
    return {
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      price: vehicle.price,
      deposit_amount: vehicle.deposit_amount || null,
      mileage: vehicle.mileage,
      condition: vehicle.condition,
      status: vehicle.status || 'on-lot',
      vehicle_type: vehicle.vehicleType || 'vehicle',
      vin: vehicle.vin,
      stock_number: vehicle.stockNumber,
      fuel_type: vehicle.fuelType,
      transmission: vehicle.transmission,
      exterior_color: vehicle.exteriorColor,
      interior_color: vehicle.interiorColor,
      engine: vehicle.engine,
      drivetrain: vehicle.drivetrain,
      description: vehicle.description,
      features: vehicle.features || [],
      images: vehicle.images || [],
      videos: vehicle.videos || []
    }
  }
}

// Export singleton instance
export const vehicleService = new VehicleService()